import React, { useState, useMemo } from 'react';
import { Modal, FormField, Button, IconButton, inputCls, DatePicker } from '../ui';
import { useConfirm } from '../../hooks/useConfirm';
import { DOW_LABELS } from '../../lib/crewOff';

// ===== CREW DAYS OFF MODAL (2026-08-06, Susie's ask) =====
// Opened from the Crew page card's "Days Off" button. Two subsystems:
//   • Weekly days off — Su–Sa toggle chips saved onto the crew member
//     (recurring_days_off, sql/077). Virtual: the board/capacity show
//     OFF on those days forever via src/lib/crewOff.js.
//   • Specific requests — date + reason, written as the SAME
//     crew_availability rows the board's right-click writes
//     (status 'off_requested', reason in note), so the board shows OFF
//     with zero new plumbing. The tally = count of requested days this
//     year; full history lists below with per-row remove.

const fmtDate = (iso) => new Date(iso + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });

export default function CrewDaysOffModal({ member, availability, setCrewAvailability, saveCrew, onClose, showToast }) {
    const { confirm, confirmElement } = useConfirm();
    const [recurring, setRecurring] = useState(() => new Set(member.recurring_days_off || []));
    const [savingRecurring, setSavingRecurring] = useState(false);
    const [recurringDirty, setRecurringDirty] = useState(false);
    const [reqDate, setReqDate] = useState('');
    const [reqEnd, setReqEnd] = useState(''); // optional — range picker (Susie: week+ vacations were one day at a time)
    const [reqReason, setReqReason] = useState('');
    const [adding, setAdding] = useState(false);

    const toggleDay = (d) => {
        setRecurring(prev => {
            const next = new Set(prev);
            if (next.has(d)) next.delete(d); else next.add(d);
            return next;
        });
        setRecurringDirty(true);
    };

    const saveRecurring = async () => {
        setSavingRecurring(true);
        try {
            await saveCrew({ ...member, recurring_days_off: [...recurring].sort() });
            setRecurringDirty(false);
            showToast(recurring.size ? `Weekly days off saved — ${[...recurring].sort().map(d => DOW_LABELS[d]).join(', ')}` : 'Weekly days off cleared');
        } catch (e) {
            showToast('Error: ' + e.message, 'error');
        } finally { setSavingRecurring(false); }
    };

    // This member's specific off rows (both request kinds), newest first.
    const offRows = useMemo(() =>
        (availability || [])
            .filter(a => a.crew_id === member.id && (a.status === 'off_requested' || a.status === 'scheduled_off'))
            .sort((a, b) => (b.date || '').localeCompare(a.date || '')),
        [availability, member.id]);

    const year = new Date().getFullYear();
    const tally = offRows.filter(a => a.status === 'off_requested' && (a.date || '').startsWith(String(year))).length;
    const todayIso = new Date().toLocaleDateString('en-CA');

    // Collapse consecutive days with the same status + reason into one
    // run ("Aug 10 – Aug 16 · 7 days · vacation") so a week off is one
    // row with one remove, not seven (Susie's range ask).
    const runs = useMemo(() => {
        const asc = [...offRows].sort((a, b) => (a.date || '').localeCompare(b.date || ''));
        const out = [];
        const nextDay = (iso) => {
            const d = new Date(iso + 'T12:00:00');
            d.setDate(d.getDate() + 1);
            return d.toLocaleDateString('en-CA');
        };
        for (const row of asc) {
            const last = out[out.length - 1];
            if (last && last.status === row.status && (last.note || '') === (row.note || '') && nextDay(last.end) === row.date) {
                last.end = row.date;
                last.rows.push(row);
            } else {
                out.push({ start: row.date, end: row.date, status: row.status, note: row.note || '', rows: [row] });
            }
        }
        return out.reverse(); // newest first
    }, [offRows]);

    // Every date in [start, end] inclusive (end optional → single day).
    const datesInRange = (startIso, endIso) => {
        const out = [];
        const d = new Date(startIso + 'T12:00:00');
        const end = new Date((endIso || startIso) + 'T12:00:00');
        while (d <= end) {
            out.push(d.toLocaleDateString('en-CA'));
            d.setDate(d.getDate() + 1);
        }
        return out;
    };

    const addRequest = async () => {
        if (!reqDate) { showToast('Pick a date', 'error'); return; }
        if (reqEnd && reqEnd < reqDate) { showToast('End date is before start date', 'error'); return; }
        const dates = datesInRange(reqDate, reqEnd);
        if (dates.length > 31) { showToast('Ranges are capped at 31 days — split longer time off', 'error'); return; }
        // Skip days already marked off instead of failing the whole range.
        const already = new Set((availability || [])
            .filter(a => a.crew_id === member.id && (a.status === 'off_requested' || a.status === 'scheduled_off'))
            .map(a => a.date));
        const toAdd = dates.filter(d => !already.has(d));
        if (toAdd.length === 0) { showToast(dates.length === 1 ? 'That day is already marked off' : 'Those days are already marked off', 'error'); return; }
        setAdding(true);
        try {
            for (const d of toAdd) {
                await setCrewAvailability(member.id, d, 'off_requested', reqReason.trim());
            }
            setReqDate('');
            setReqEnd('');
            setReqReason('');
            const skipped = dates.length - toAdd.length;
            showToast(`${toAdd.length} day${toAdd.length === 1 ? '' : 's'} off recorded${skipped ? ` (${skipped} already off)` : ''} — the board shows OFF`);
        } catch (e) {
            showToast('Error: ' + e.message, 'error');
        } finally { setAdding(false); }
    };

    const removeRun = async (run) => {
        const days = run.rows.length;
        const label = days === 1 ? fmtDate(run.start) : `${fmtDate(run.start)} – ${fmtDate(run.end)} (${days} days)`;
        if (!(await confirm({ title: days === 1 ? `Remove ${member.name}'s day off?` : `Remove ${member.name}'s ${days} days off?`, body: `${label} is cleared and ${member.name} shows as available on the board again. Any reason noted for these days is dropped.`, confirmLabel: days === 1 ? 'Remove day off' : 'Remove days off', danger: true }))) return;
        try {
            for (const row of run.rows) {
                await setCrewAvailability(member.id, row.date, null);
            }
            showToast(days === 1 ? 'Day off removed' : `${days} days off removed`);
        } catch (e) {
            showToast('Error: ' + e.message, 'error');
        }
    };

    return (
        <Modal title={`Days Off — ${member.name}`} onClose={onClose} width="max-w-xl">
            <div className="space-y-5">
                {/* Weekly recurring */}
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                    <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2">Weekly Days Off <span className="normal-case font-medium text-slate-400">(part-time schedule — repeats every week)</span></div>
                    <div className="flex gap-1.5 flex-wrap">
                        {DOW_LABELS.map((label, d) => (
                            <button key={d} type="button" onClick={() => toggleDay(d)}
                                className={`w-11 py-2 rounded-lg text-xs font-bold ${recurring.has(d) ? 'bg-red-600 text-white' : 'bg-white border border-slate-300 text-slate-600 hover:bg-slate-100'}`}>
                                {label}
                            </button>
                        ))}
                    </div>
                    <div className="flex items-center justify-between mt-2.5">
                        <span className="text-xs text-slate-500">
                            {recurring.size ? `Off every ${[...recurring].sort().map(d => DOW_LABELS[d]).join(', ')} — shows OFF on the board automatically.` : 'No weekly days off set.'}
                        </span>
                        <Button loading={savingRecurring} onClick={saveRecurring} disabled={!recurringDirty}>{recurringDirty ? 'Save' : 'Saved'}</Button>
                    </div>
                </div>

                {/* Specific requests */}
                <div>
                    <div className="flex items-baseline justify-between mb-2">
                        <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Request Specific Days Off</span>
                        <span className="text-xs font-bold text-slate-700 bg-slate-100 px-2 py-0.5 rounded-full">{tally} requested in {year}</span>
                    </div>
                    <div className="flex items-end gap-2 flex-wrap">
                        <FormField label="Date">
                            <DatePicker value={reqDate} onChange={v => setReqDate(v)} placeholder="Pick a date…" />
                        </FormField>
                        <FormField label="Through (optional)">
                            <DatePicker value={reqEnd} min={reqDate || undefined} onChange={v => setReqEnd(v)} placeholder="Same day" />
                        </FormField>
                        <div className="flex-1 min-w-[160px]">
                            <FormField label="What for">
                                <input className={inputCls} value={reqReason} onChange={e => setReqReason(e.target.value)} placeholder="Dentist, wedding, vacation…" />
                            </FormField>
                        </div>
                        <Button loading={adding} onClick={addRequest}>{reqEnd && reqDate && reqEnd > reqDate ? `Add ${datesInRange(reqDate, reqEnd).length} Days` : 'Add'}</Button>
                    </div>

                    {runs.length === 0 ? (
                        <p className="text-sm text-slate-400 py-3 text-center">No days off on record.</p>
                    ) : (
                        <div className="mt-3 space-y-1.5 max-h-60 overflow-y-auto pr-1">
                            {runs.map(run => (
                                <div key={run.start} className={`flex items-center gap-3 border rounded-lg px-3 py-2 ${run.end >= todayIso ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50 opacity-70'}`}>
                                    <div className="flex-1 min-w-0">
                                        <span className="text-sm font-semibold text-slate-800">
                                            {run.start === run.end ? fmtDate(run.start) : `${fmtDate(run.start)} – ${fmtDate(run.end)}`}
                                        </span>
                                        {run.rows.length > 1 && <span className="ml-2 text-[10px] font-bold text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded-full">{run.rows.length} days</span>}
                                        {run.status === 'scheduled_off' && <span className="ml-2 text-[10px] font-bold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded-full uppercase">Scheduled Off</span>}
                                        {run.note && <span className="block text-xs text-slate-500 truncate">{run.note}</span>}
                                    </div>
                                    <IconButton label="Remove" size="sm" tone="danger" onClick={() => removeRun(run)}>✕</IconButton>
                                </div>
                            ))}
                        </div>
                    )}
                    <p className="text-[11px] text-slate-400 mt-2">Saved days show as OFF on the dispatch board immediately. Assigning a job on an OFF day asks for a confirm first.</p>
                </div>
            </div>
            {confirmElement}
        </Modal>
    );
}
